import { useEffect, useRef, useState } from 'react';
import {
  MESSAGE_ATTACHMENT_ACCEPT,
  filterMessageAttachmentFiles,
  messageAttachmentNames,
  messagePreviewText,
} from '../lib/messageAttachments.js';
import {
  MESSAGE_EMOJI_OPTIONS,
  createSpeechRecognizer,
  insertTextAtSelection,
  speechRecognitionSupported,
} from '../lib/messageComposerExtras.js';
import {
  createLocalFileObjectUrl,
  resolveUploadBlobUrl,
} from '../lib/uploadBlobStore.js';
import { ViewableFileName } from './EvidenceUpload.jsx';
import TrashIcon from './TrashIcon.jsx';

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M3.4 11.2 20.2 3.6c.7-.3 1.4.4 1.1 1.1l-7.6 16.8c-.3.7-1.3.6-1.5-.2l-1.8-6.3-6.3-1.8c-.8-.2-.9-1.2-.2-1.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function PaperclipIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        d="M16.5 6.5v9.2a4.5 4.5 0 0 1-9 0V7.8a3 3 0 0 1 6 0v7.4a1.5 1.5 0 0 1-3 0V8.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EmojiIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <circle cx="12" cy="12" r="8.25" fill="none" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="9" cy="10.25" r="1" fill="currentColor" />
      <circle cx="15" cy="10.25" r="1" fill="currentColor" />
      <path
        d="M8.75 14c.85 1.2 2 1.85 3.25 1.85S14.4 15.2 15.25 14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <rect
        x="9"
        y="3.5"
        width="6"
        height="11"
        rx="3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path
        d="M6.5 11.5a5.5 5.5 0 0 0 11 0M12 17v3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, [query]);

  return matches;
}

export function MessageAttachments({ message }) {
  const names = messageAttachmentNames(message);
  const [urls, setUrls] = useState({});
  const nameKey = names.join('\u0000');
  const messageId = String(message?.ROWID || '');

  useEffect(() => {
    let cancelled = false;
    const created = [];

    if (!messageId || !names.length) {
      setUrls({});
      return undefined;
    }

    (async () => {
      const next = {};
      for (const name of names) {
        const url = await resolveUploadBlobUrl('message', messageId, name);
        if (url) {
          created.push(url);
          next[name] = url;
        }
      }
      if (!cancelled) setUrls(next);
    })();

    return () => {
      cancelled = true;
      for (const url of created) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
      }
    };
    // names derived from nameKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, nameKey]);

  if (!names.length) return null;
  return (
    <ul className="message-attachment-list">
      {names.map((name) => (
        <li key={name}>
          <ViewableFileName name={name} url={urls[name]} className="message-attachment" />
        </li>
      ))}
    </ul>
  );
}

function PendingMessageAttachment({ file, pending, onRemove }) {
  const [url, setUrl] = useState(null);
  const name = String(file?.name || '').trim();

  useEffect(() => {
    const objectUrl = createLocalFileObjectUrl(file);
    setUrl(objectUrl);
    return () => {
      if (objectUrl) {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch {
          // ignore
        }
      }
    };
  }, [file]);

  if (!name) return null;

  return (
    <li>
      <ViewableFileName name={name} url={url} className="message-attachment" />
      <button
        type="button"
        className="text-link"
        disabled={pending}
        onClick={() => onRemove?.(name)}
      >
        Remove
      </button>
    </li>
  );
}

function formatMessageWhen(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

/** One message bubble with optional trash-can delete. */
export function MessageBubble({
  message,
  showTime = true,
  canDelete = false,
  deleting = false,
  deleteDisabled = false,
  onDelete,
}) {
  if (!message) return null;
  const role = String(message.Sender_Role || '').toLowerCase();

  function confirmDelete() {
    if (!canDelete || deleting || deleteDisabled || !onDelete) return;
    const preview = messagePreviewText(message).trim();
    const snippet = preview.length > 80 ? `${preview.slice(0, 77)}…` : preview;
    const ok = window.confirm(
      snippet ? `Delete this message?\n\n“${snippet}”` : 'Delete this message?'
    );
    if (!ok) return;
    onDelete(message);
  }

  return (
    <article className={`message-bubble role-${role}`}>
      <header>
        <strong>{message.Sender_Name || message.Sender_Role}</strong>
        {showTime ? <time>{formatMessageWhen(message.Created_At)}</time> : null}
      </header>
      <div className="message-bubble-content">
        <div className="message-bubble-body">
          {message.Body ? <p>{message.Body}</p> : null}
          <MessageAttachments message={message} />
        </div>
        {canDelete ? (
          <button
            type="button"
            className="message-delete"
            aria-label={deleting ? 'Deleting message' : 'Delete message'}
            title="Delete message"
            disabled={Boolean(deleting) || deleteDisabled}
            onClick={confirmDelete}
          >
            {deleting ? (
              <span className="message-delete-busy" aria-hidden="true">
                …
              </span>
            ) : (
              <TrashIcon size={16} />
            )}
          </button>
        ) : null}
      </div>
    </article>
  );
}

export default function MessageComposer({
  label = 'Message your attorney',
  placeholder = 'Ask a question or share an update…',
  draft,
  onDraftChange,
  attachmentFiles = [],
  onAttachmentFilesChange,
  /** @deprecated Prefer attachmentFiles */
  attachmentNames = [],
  /** @deprecated Prefer onAttachmentNamesChange */
  onAttachmentNamesChange,
  onSubmit,
  pending = false,
  error = '',
  canSend = true,
  rows = 3,
  submitLabel = 'Send message',
  pendingLabel = 'Sending…',
  showAttachments = true,
}) {
  const fileRef = useRef(null);
  const textareaRef = useRef(null);
  const emojiMenuRef = useRef(null);
  const recognitionRef = useRef(null);
  const baseDraftRef = useRef(String(draft || ''));
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceHint, setVoiceHint] = useState('');
  const voiceSupported = speechRecognitionSupported();
  const isMobileLayout = useMediaQuery('(max-width: 900px)');
  const files = (attachmentFiles || []).length
    ? attachmentFiles
    : (attachmentNames || []).map((name) => ({ name: String(name) }));
  const hasText = Boolean(String(draft || '').trim());
  const hasFiles = files.length > 0;
  const disabled = pending || !canSend || (!hasText && !hasFiles);
  const notifyChange = onAttachmentFilesChange || onAttachmentNamesChange;
  const resolvedRows = isMobileLayout ? Math.min(Number(rows) || 2, 2) : rows;
  const resolvedLabel = isMobileLayout ? '' : label;
  const resolvedSubmitLabel = isMobileLayout
    ? submitLabel === 'Send message'
      ? 'Send'
      : submitLabel
    : submitLabel;

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, resolvedRows]);

  useEffect(() => {
    if (!emojiOpen) return undefined;
    function onPointerDown(event) {
      if (emojiMenuRef.current?.contains(event.target)) return;
      setEmojiOpen(false);
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') setEmojiOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [emojiOpen]);

  useEffect(
    () => () => {
      try {
        recognitionRef.current?.stop();
      } catch {
        // ignore
      }
      recognitionRef.current = null;
    },
    []
  );

  function addFiles(fileList) {
    const accepted = filterMessageAttachmentFiles(fileList);
    if (!accepted.length) return;
    notifyChange?.(accepted, { mode: 'add' });
  }

  function applyDraftInsert(insert) {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? String(draft || '').length;
    const end = el?.selectionEnd ?? start;
    const next = insertTextAtSelection(draft, insert, start, end);
    onDraftChange?.(next.value);
    requestAnimationFrame(() => {
      const target = textareaRef.current;
      if (!target) return;
      target.focus();
      try {
        target.setSelectionRange(next.selectionStart, next.selectionEnd);
      } catch {
        // ignore
      }
    });
  }

  function onPickEmoji(emoji) {
    applyDraftInsert(emoji);
    setEmojiOpen(false);
  }

  function stopListening({ clearHint = false } = {}) {
    try {
      recognitionRef.current?.stop();
    } catch {
      // ignore
    }
    recognitionRef.current = null;
    setListening(false);
    if (clearHint) setVoiceHint('');
  }

  function startListening() {
    if (pending || !canSend) return;
    if (!voiceSupported) {
      setVoiceHint('Voice dictation is not supported in this browser.');
      return;
    }
    setVoiceHint('');
    setEmojiOpen(false);
    baseDraftRef.current = String(draft || '');
    const recognition = createSpeechRecognizer({
      onResult: ({ finalChunk, interimChunk }) => {
        if (finalChunk) {
          const base = baseDraftRef.current;
          const spacer = base && !/\s$/.test(base) ? ' ' : '';
          baseDraftRef.current = `${base}${spacer}${finalChunk.trim()}`;
        }
        const interim = String(interimChunk || '').trim();
        const base = baseDraftRef.current;
        const spacer = base && interim && !/\s$/.test(base) ? ' ' : '';
        onDraftChange?.(`${base}${spacer}${interim}`);
      },
      onError: (event) => {
        const code = String(event?.error || '');
        if (code === 'not-allowed' || code === 'service-not-allowed') {
          setVoiceHint('Microphone permission is blocked. Allow mic access to dictate.');
        } else if (code === 'no-speech') {
          setVoiceHint('No speech detected. Tap the mic and try again.');
        } else if (code && code !== 'aborted') {
          setVoiceHint('Voice dictation stopped. Tap the mic to try again.');
        }
        stopListening();
      },
      onEnd: () => {
        setListening(false);
        recognitionRef.current = null;
      },
    });
    if (!recognition) {
      setVoiceHint('Voice dictation is not supported in this browser.');
      return;
    }
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch {
      setVoiceHint('Could not start voice dictation.');
      stopListening();
    }
  }

  function toggleListening() {
    if (listening) {
      stopListening({ clearHint: true });
      return;
    }
    startListening();
  }

  function onComposerKeyDown(e) {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent?.isComposing) return;
    e.preventDefault();
    if (disabled) return;
    if (listening) stopListening({ clearHint: true });
    const form = e.currentTarget.form;
    if (typeof form?.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }
    form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }

  function handleSubmit(e) {
    if (listening) stopListening({ clearHint: true });
    onSubmit?.(e);
  }

  return (
    <form className="message-composer messages-composer" onSubmit={handleSubmit}>
      <div className="field">
        {resolvedLabel ? <span>{resolvedLabel}</span> : null}
        <div className="message-composer-input-shell">
          {showAttachments ? (
            <>
              <input
                ref={fileRef}
                type="file"
                className="sr-only"
                multiple
                accept={MESSAGE_ATTACHMENT_ACCEPT}
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                className="message-composer-tool"
                disabled={pending}
                aria-label="Add attachment"
                title="Add attachment"
                onClick={() => fileRef.current?.click()}
              >
                <PaperclipIcon />
              </button>
            </>
          ) : null}

          <textarea
            ref={textareaRef}
            className="message-composer-input"
            rows={resolvedRows}
            value={draft}
            onChange={(e) => {
              if (listening) stopListening({ clearHint: true });
              onDraftChange?.(e.target.value);
            }}
            onKeyDown={onComposerKeyDown}
            placeholder={placeholder}
            aria-label={label || placeholder || 'Message'}
            aria-keyshortcuts="Enter"
            title="Press Enter to send · Shift+Enter for a new line"
          />

          <div className="message-composer-tools" ref={emojiMenuRef}>
            <button
              type="button"
              className={`message-composer-tool${emojiOpen ? ' is-active' : ''}`}
              disabled={pending}
              aria-label="Insert emoji"
              title="Insert emoji"
              aria-expanded={emojiOpen}
              aria-haspopup="dialog"
              onClick={() => {
                if (listening) stopListening({ clearHint: true });
                setEmojiOpen((open) => !open);
              }}
            >
              <EmojiIcon />
            </button>
            {emojiOpen ? (
              <div className="message-emoji-picker" role="dialog" aria-label="Emoji picker">
                {MESSAGE_EMOJI_OPTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className="message-emoji-option"
                    onClick={() => onPickEmoji(emoji)}
                  >
                    <span aria-hidden="true">{emoji}</span>
                    <span className="sr-only">Insert {emoji}</span>
                  </button>
                ))}
              </div>
            ) : null}

            <button
              type="button"
              className={`message-composer-tool${listening ? ' is-listening' : ''}`}
              disabled={pending || (!voiceSupported && !listening)}
              aria-label={listening ? 'Stop voice dictation' : 'Dictate with voice'}
              title={
                listening
                  ? 'Stop listening'
                  : voiceSupported
                    ? 'Dictate with voice'
                    : 'Voice dictation not supported in this browser'
              }
              aria-pressed={listening}
              onClick={toggleListening}
            >
              {listening ? (
                <span className="message-composer-mic-pulse" aria-hidden="true" />
              ) : null}
              <MicIcon />
            </button>

            <button
              type="submit"
              className="message-composer-send-icon"
              disabled={disabled}
              aria-label={pending ? pendingLabel : resolvedSubmitLabel}
              title={pending ? pendingLabel : resolvedSubmitLabel}
            >
              {pending ? <span className="message-composer-send-pending">…</span> : <SendIcon />}
            </button>
          </div>
        </div>
      </div>

      {showAttachments && hasFiles ? (
        <div className="message-composer-attachments">
          <ul className="message-pending-attachments">
            {files.map((file) => (
              <PendingMessageAttachment
                key={file.name}
                file={file}
                pending={pending}
                onRemove={(name) => notifyChange?.([name], { mode: 'remove' })}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {listening ? (
        <p className="message-composer-voice-status is-listening" aria-live="polite">
          <span className="message-composer-voice-dot" aria-hidden="true" />
          Recording… tap the mic to stop.
        </p>
      ) : null}
      {voiceHint ? <p className="message-composer-voice-hint">{voiceHint}</p> : null}
      {error ? <p className="form-error">{error}</p> : null}
    </form>
  );
}
